// Admin Panel - Staging/Production Environment Support
import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DashboardSkeleton, UserListSkeleton } from './components/ui/LoadingSkeleton';

// Eagerly loaded pages (critical path)
import { LoginPage } from './pages/LoginPage';
import { CreateAccountPage } from './pages/CreateAccountPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';

// Lazy loaded pages (code splitting)
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const RidesPage = lazy(() => import('./pages/RidesPage').then(m => ({ default: m.RidesPage })));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const UserGaragePage = lazy(() => import('./pages/UserGaragePage').then(m => ({ default: m.UserGaragePage })));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage').then(m => ({ default: m.LoyaltyPage })));
const IssuesPage = lazy(() => import('./pages/IssuesPage'));
const PointsManagerPage = lazy(() => import('./pages/PointsManagerPage'));
const NewsPage = lazy(() => import('./pages/NewsPage').then(m => ({ default: m.NewsPage })));
const StoresPage = lazy(() => import('./pages/StoresPage').then(m => ({ default: m.StoresPage })));
const StoreDetailsPage = lazy(() => import('./pages/StoreDetailsPage').then(m => ({ default: m.StoreDetailsPage })));
const ClubsPage = lazy(() => import('./pages/ClubsPage').then(m => ({ default: m.ClubsPage })));
const ClubDetailsPage = lazy(() => import('./pages/ClubDetailsPage').then(m => ({ default: m.ClubDetailsPage })));
const ClubMembersPage = lazy(() => import('./pages/ClubMembersPage').then(m => ({ default: m.ClubMembersPage })));
const ClubNewsManagementPage = lazy(() => import('./pages/ClubNewsManagementPage').then(m => ({ default: m.ClubNewsManagementPage })));
const ClubMembershipPaymentsPage = lazy(() => import('./pages/ClubMembershipPaymentsPage').then(m => ({ default: m.ClubMembershipPaymentsPage })));
const MarketingPage = lazy(() => import('./pages/MarketingPage').then(m => ({ default: m.MarketingPage })));
const EmailComposerPage = lazy(() => import('./pages/EmailComposerPage').then(m => ({ default: m.EmailComposerPage })));
const AISuggestionsPage = lazy(() => import('./pages/AISuggestionsPage').then(m => ({ default: m.AISuggestionsPage })));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const ContactSupportPage = lazy(() => import('./pages/ContactSupportPage').then(m => ({ default: m.ContactSupportPage })));
const SupportInboxPage = lazy(() => import('./pages/SupportInboxPage'));

// Create a React Query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus by default (can be enabled per query)
      refetchOnWindowFocus: false,
      // Retry failed requests once
      retry: 1,
      // Consider data stale after 5 minutes
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Loading fallback component for Suspense
 */
function PageLoadingFallback() {
  return (
    <div className="p-6">
      <DashboardSkeleton />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <AnalyticsTracker />
              <Routes>
                {/* Backward compatibility: redirect /login to /store */}
                <Route path="/login" element={<Navigate to="/store" replace />} />
                <Route path="/store" element={<LoginPage />} />
                <Route path="/create-account" element={<CreateAccountPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/payment/success"
                  element={
                    <Suspense fallback={<PageLoadingFallback />}>
                      <PaymentSuccessPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Routes>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/dashboard" element={<DashboardPage />} />
                              <Route path="/users/riders" element={<UsersPage />} />
                              <Route path="/users/store-managers" element={<UsersPage />} />
                              <Route path="/users/club-admins" element={<UsersPage />} />
                              <Route path="/users" element={<Navigate to="/users/riders" replace />} />
                              <Route path="/rides" element={<RidesPage />} />
                              <Route path="/templates" element={<TemplatesPage />} />
                              <Route path="/analytics" element={<AnalyticsPage />} />
                              <Route path="/analytics/user-garage" element={<UserGaragePage />} />
                              <Route path="/loyalty" element={<LoyaltyPage />} />
                              <Route path="/loyalty/points-manager" element={<PointsManagerPage />} />
                              <Route path="/issues" element={<IssuesPage />} />
                              <Route path="/news" element={<NewsPage />} />
                              <Route path="/marketing" element={<MarketingPage />} />
                              <Route path="/marketing/email" element={<EmailComposerPage />} />
                              <Route path="/marketing/ai-suggestions" element={<AISuggestionsPage />} />
                              <Route path="/stores" element={<StoresPage />} />
                              <Route path="/stores/:id" element={<StoreDetailsPage />} />
                              <Route path="/clubs" element={<ClubsPage />} />
                              <Route path="/clubs/:id" element={<ClubDetailsPage />} />
                              <Route path="/clubs/:id/members" element={<ClubMembersPage />} />
                              <Route path="/clubs/:id/news" element={<ClubNewsManagementPage />} />
                              <Route path="/clubs/:id/payments" element={<ClubMembershipPaymentsPage />} />
                              <Route path="/help/faq" element={<FAQPage />} />
                              <Route path="/help/contact" element={<ContactSupportPage />} />
                              <Route path="/help/inbox" element={<SupportInboxPage />} />
                            </Routes>
                          </Suspense>
                        </ErrorBoundary>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
              <Toaster position="top-right" />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
